/**
 * AiCreditsBadge — a compact, always-visible AI-credit balance in the shell
 * header (next to the notification bell). Reads the shared wallet store (kept
 * fresh by {@link CreditGuardHost}), tints amber/red as the balance approaches
 * the tenant's low-balance threshold, and links to the full credits control
 * centre (/settings/credits). Hidden until the wallet has loaded so it never
 * flashes a wrong number; hidden entirely if the wallet service is unreachable.
 */
import { useNavigate } from 'react-router';

import { isUnlimitedWallet } from '@/lib/api/credits';
import { useCreditsStore } from './useCreditsStore';
import { useT, intlLocale } from '@/i18n';

function fmtCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toLocaleString(intlLocale('fr-FR'), { maximumFractionDigits: 1 })}M`;
  if (value >= 10_000) return `${Math.round(value / 1000)}k`;
  if (value >= 1000) return `${(value / 1000).toLocaleString(intlLocale('fr-FR'), { maximumFractionDigits: 1 })}k`;
  return value.toLocaleString(intlLocale('fr-FR'), { maximumFractionDigits: value < 10 ? 1 : 0 });
}

function fmtResetDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(intlLocale('fr-FR'), { day: 'numeric', month: 'short' });
}

export function AiCreditsBadge() {
  const { t } = useT();
  const navigate = useNavigate();
  const { wallet, cycle, loaded } = useCreditsStore();

  // Don't render until we have a real wallet (avoids a flash of "0" / wrong tint).
  if (!loaded || !wallet) return null;

  // Unlimited (superadmin/global-admin): show ∞, never a number or a low-balance
  // tint, and never the "Recharger" nudge — there is nothing to recharge.
  const unlimited = isUnlimitedWallet(wallet);
  const available = wallet.available ?? 0;
  const threshold = wallet.low_balance_threshold ?? 0;
  const critical = !unlimited && available <= threshold;
  const warn = !unlimited && !critical && available <= threshold * 2;

  if (unlimited) {
    const titleUnlimited = t('shell.features.creditsAiCreditsBadge.aiCreditsUnlimitedSuperadminAccount');
    return (
      <button
        type="button"
        onClick={() => navigate('/settings/credits')}
        title={titleUnlimited}
        aria-label={titleUnlimited}
        className="flex items-center gap-1.5 rounded-token border border-border bg-surface-2/40 px-2 py-1.5 text-sm font-medium tabular-nums text-foreground shadow-elevation-1 transition hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8v8M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.4-1.1 2-2.5 2.5s-2.5 1.1-2.5 2.5a2.5 2.5 0 0 0 5 0" />
        </svg>
        <span className="hidden sm:inline" aria-hidden="true">∞</span>
        <span className="hidden text-[11px] font-normal opacity-70 md:inline">{t('shell.features.creditsAiCreditsBadge.unlimited')}</span>
      </button>
    );
  }

  const tone = critical
    ? 'border-danger/40 bg-danger/10 text-danger hover:bg-danger/15'
    : warn
      ? 'border-warning/40 bg-warning/10 text-warning hover:bg-warning/15'
      : 'border-border bg-surface-2/40 text-foreground hover:bg-surface-2';

  const pctUsed = cycle && cycle.allowance > 0 ? Math.round(cycle.pctUsed) : null;
  const resetLabel = fmtResetDate(cycle?.resetDate ?? null);

  const title = critical
    ? t('shell.features.creditsAiCreditsBadge.aiCreditsLowBalanceClickTo')
    : [
        t('shell.features.creditsAiCreditsBadge.aiCreditsAvailableAvailable', { available: available.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) }),
        pctUsed != null ? t('shell.features.creditsAiCreditsBadge.pctusedUsedThisCycle', { pctUsed }) : null,
        resetLabel ? t('shell.features.creditsAiCreditsBadge.resetsOnResetlabel', { resetLabel }) : null,
      ]
        .filter(Boolean)
        .join(' · ');

  return (
    <button
      type="button"
      data-testid="ai-credits-badge"
      data-available={available}
      onClick={() => navigate('/settings/credits')}
      title={title}
      aria-label={title}
      className={`flex items-center gap-1.5 rounded-token border px-2 py-1.5 text-sm font-medium tabular-nums shadow-elevation-1 transition focus:outline-none focus:ring-2 focus:ring-ring ${tone}`}
    >
      {/* coin/spark glyph */}
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v8M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.4-1.1 2-2.5 2.5s-2.5 1.1-2.5 2.5a2.5 2.5 0 0 0 5 0" />
      </svg>
      <span className="hidden sm:inline">{fmtCompact(available)}</span>
      <span className="hidden text-[11px] font-normal opacity-70 md:inline">{t('shell.features.creditsAiCreditsBadge.cr')}</span>
      {pctUsed != null && !critical && (
        <span className="hidden text-[11px] font-normal opacity-70 lg:inline">
          · {pctUsed}%
        </span>
      )}
      {critical && (
        <span className="ml-0.5 rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-semibold text-white">{t('shell.features.creditsAiCreditsBadge.topUp')}</span>
      )}
    </button>
  );
}
